/**
 * EntityIcon — a compact, right-sized tinted tile for an entity kind.
 *
 * This is the app's canonical entity glyph. It deliberately replaces the
 * oversized `w-10 h-10` + `w-5 h-5` + `ring-2 shadow-md` tiles that made the
 * old inbox feel clunky — one `size` knob controls the treatment everywhere.
 */

import type { TaskEntityKind } from '@/types/schema';
import { getEntityTheme } from '@/config/entityThemes';

type EntityIconSize = 'sm' | 'md' | 'lg';

const SIZES: Record<EntityIconSize, { tile: string; icon: string; radius: string }> = {
  sm: { tile: 'w-7 h-7', icon: 'w-3.5 h-3.5', radius: 'rounded-lg' },
  md: { tile: 'w-8 h-8', icon: 'w-4 h-4', radius: 'rounded-xl' },
  lg: { tile: 'w-10 h-10', icon: 'w-5 h-5', radius: 'rounded-xl' },
};

interface EntityIconProps {
  kind?: TaskEntityKind | null;
  size?: EntityIconSize;
  className?: string;
}

export function EntityIcon({ kind, size = 'md', className = '' }: EntityIconProps) {
  const theme = getEntityTheme(kind);
  const s = SIZES[size];
  const Icon = theme.Icon;

  return (
    <div
      className={`${s.tile} ${s.radius} ${theme.tile} ring-1 ${theme.ring} flex items-center justify-center shrink-0 ${className}`}
    >
      <Icon className={s.icon} strokeWidth={2.2} />
    </div>
  );
}
